import React, { useContext, useMemo } from 'react';
import { ClientDocSpaceMenu, ClientDocLeafMenu } from '../types';
import { PageContext } from '../frontend/pageContext';
import MenuLink from './MenuLink';
import MenuGroup from './MenuGroup';
import { findObjectByPath } from './utils';

interface Props {
  menu: ClientDocSpaceMenu | ClientDocLeafMenu;
}

const findRootParent = (menu) => {
  if (menu.parent && menu.parent.type !== 'docSpaceMenu') {
    return findRootParent(menu.parent);
  }
  return menu.docLeafId;
};

const findRootMenu = (menu, pathname) => {
  const obj = findObjectByPath(pathname, menu);
  if (obj) {
    return findRootParent(obj);
  }
  return null;
};

const Menu = ({ menu }: Props) => {
  const { isStatic, relPath, docSpace, spacePdfExists, docLeaf, pathname } =
    useContext(PageContext);
  const computedMenuRoot = useMemo(
    () => findRootMenu(menu, pathname),
    [menu, pathname],
  );
  const menuRoot = pathname.split('/')[1] !== 'd' ? computedMenuRoot : null;

  if (menu.type === 'docLeafMenu' && menu.children.length > 1) {
    menu.children.sort((x, y) => {
      if (x.type === 'menuLink' && y.type === 'menuLink') {
        if (x.url === 'index' && y.url === 'index') return 0;
        if (x.url === 'index') return -1;
        if (y.url === 'index') return 1;
      }
      return 0;
    });
  }

  return (
    <div
      className={`main${docSpace && spacePdfExists ? ' pdf-available' : ''}`}
      id="js-menu"
      data-test-id="menu"
    >
      {docSpace && (
        <h3
          id={docSpace.title}
          data-test-id="docspace-menu-title"
          className="doc-object-title"
        >
          <a href={isStatic ? `${relPath}/index.html` : docSpace.path}>
            {docSpace.title}
          </a>
        </h3>
      )}
      {!docSpace && docLeaf && (
        <h3
          id={docLeaf.title}
          data-test-id="docleaf-menu-title"
          className="doc-object-title"
        >
          <a href={isStatic ? `${relPath}/pages/index.html` : docLeaf.path}>
            {docLeaf.title}
          </a>
        </h3>
      )}
      <ul>
        {(menu.children as any).map((menuItem, i) => {
          if (
            menuItem.type === 'menuLink' ||
            menuItem.type === 'externalMenuLink'
          ) {
            return (
              <MenuLink
                menuLink={menuItem}
                index={i}
                key={`mlink-${i}`}
                menuRoot={menuRoot}
              />
            );
          } else if (menuItem.type === 'menuGroup') {
            return (
              <MenuGroup
                menuGroup={menuItem}
                index={i}
                key={`mgroup-${i}`}
                singleDocLeaf={menu.children.length === 1}
                menuRoot={menuRoot}
              />
            );
          } else {
            return <></>;
          }
        })}
      </ul>
    </div>
  );
};
export default Menu;
